import type { Pt } from '../engine/level';

export type InputHandlers = {
  onStart(p: Pt): void;
  onMove(p: Pt): void;
  onEnd(): void;
};

/** 한 포인터만 추적. 취소·이탈도 "손 뗌"으로 처리(선을 버리지 않는다 — 명세 §4.5). */
/** clientX/Y → 요소 로컬 좌표. 세로 폰에서 요소를 90° 돌려 놓은 경우를 처리한다. */
export function clientToLocal(el: HTMLElement, clientX: number, clientY: number): [number, number] {
  const r = el.getBoundingClientRect();
  if (el.closest('.rotated')) {
    // transform: rotate(90deg) translateY(-100%) → 화면 X = h − y, Y = x  (h = 요소의 회전 전 높이 = 화면 폭)
    return [clientY - r.top, r.width - (clientX - r.left)];
  }
  return [clientX - r.left, clientY - r.top];
}

export function attachInput(el: HTMLElement, toWorld: (sx: number, sy: number) => Pt, h: InputHandlers): () => void {
  let active: number | null = null;
  const pos = (e: PointerEvent): Pt => {
    const [x, y] = clientToLocal(el, e.clientX, e.clientY);
    return toWorld(x, y);
  };
  const down = (e: PointerEvent) => {
    if (active !== null) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    active = e.pointerId;
    el.setPointerCapture?.(e.pointerId);
    e.preventDefault();
    h.onStart(pos(e));
  };
  const move = (e: PointerEvent) => {
    if (e.pointerId !== active) return;
    e.preventDefault();
    const evs = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
    if (evs.length) for (const c of evs) h.onMove(pos(c));
    else h.onMove(pos(e));
  };
  const up = (e: PointerEvent) => {
    if (e.pointerId !== active) return;
    active = null;
    e.preventDefault();
    h.onEnd();
  };
  const blur = () => { if (active !== null) { active = null; h.onEnd(); } };
  el.addEventListener('pointerdown', down);
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerup', up);
  el.addEventListener('pointercancel', up);
  el.addEventListener('lostpointercapture', up as EventListener);
  window.addEventListener('blur', blur);
  const stop = (e: Event) => e.preventDefault();
  el.addEventListener('contextmenu', stop);
  document.addEventListener('gesturestart', stop);
  return () => {
    el.removeEventListener('pointerdown', down);
    el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerup', up);
    el.removeEventListener('pointercancel', up);
    el.removeEventListener('lostpointercapture', up as EventListener);
    window.removeEventListener('blur', blur);
    el.removeEventListener('contextmenu', stop);
    document.removeEventListener('gesturestart', stop);
  };
}
